import { useEffect, useState } from "react"
import "./JobColor.css"
import Job from "../models/Job"
import ColorSelector from "../../../../components/color/ColorSelector"
import { patchJob, useHub } from "../../../../core/Global"
import { API_ROUTES } from "../../../../core/Constants"

interface IProps {
    job: Job
    variant?: "icon" | "spine" | "bar"
    size?: number
    className?: string
    alwaysVisible?: boolean
}

/**
 * Binds the shared ColorSelector to a job: what a colour means here is "the job's board colour",
 * persisted through crm/jobs/setcolor, which pushes the updated job to everyone over the live hub.
 */
export default function JobColorPicker(props: IProps) {

    const hub = useHub()

    const [saving, savingSet] = useState<boolean>(false)

    // Echo of the colour just picked, so the trigger reacts even for jobs that aren't in the board
    // bank (closed / archived ones), where patchJob has nothing to update.
    const [echo, echoSet] = useState<string | null>()

    useEffect(() => {
        if (echo !== undefined && (props.job.color ?? null) === echo) echoSet(undefined)
    }, [props.job.color])

    const color = echo !== undefined ? (echo ?? undefined) : props.job.color

    const apply = (value?: string) => {
        const previous = props.job.color

        echoSet(value ?? null)
        // Repaint the row/band/drawer for THIS user right away. Everyone else (and this user again,
        // authoritatively) gets it from the live-hub push the endpoint fires.
        patchJob(props.job.id, { color: value })
        savingSet(true)

        hub.Post(API_ROUTES.Jobs + "setcolor", {
            jobId: props.job.id,
            color: value ?? ""
        }, res => {
            savingSet(false)
            if (!res.success) {
                echoSet(undefined)
                patchJob(props.job.id, { color: previous })
            }
        })
    }

    return (
        <ColorSelector id={`job-${props.job.id}`}
            title="Job Color"
            subtitle={`#${props.job.jobId}`}
            color={color}
            onSelect={apply}
            variant={props.variant}
            size={props.size}
            className={props.className}
            alwaysVisible={props.alwaysVisible}
            saving={saving} />
    )
}
